// src/components/Auth/AuthCallback.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import mudoLogo from "../../assets/mudo-logo-bg.png";
import { motion } from "framer-motion";

function getFlowTypeFromUrl() {
  try {
    const url = new URL(window.location.href);

    // Supabase usually encodes type in the hash, but sometimes in search
    const searchType = url.searchParams.get("type");
    if (searchType) return searchType;

    if (url.hash && url.hash.includes("type=")) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      return hashParams.get("type");
    }
  } catch {
    // ignored
  }
  return null;
}

/**
 * AuthCallback - Handles magic link, email confirmation, and password recovery.
 * After user clicks email link, Supabase redirects here with auth tokens.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | set-password | success | error
  const [error, setError] = useState("");
  const [flowType, setFlowType] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [newPassConfirm, setNewPassConfirm] = useState("");
  const [passError, setPassError] = useState("");

  const MIN_PASS_LEN = 8;

  useEffect(() => {
    const t = getFlowTypeFromUrl();
    setFlowType(t);
    handleAuthCallback(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flowLabel = useMemo(() => {
    if (flowType === "recovery") return "Resetting your password";
    if (flowType === "signup") return "Confirming your email";
    if (flowType === "magiclink") return "Verifying your login";
    return "Verifying your login";
  }, [flowType]);

  async function handleAuthCallback(typeFromUrl) {
    try {
      if (!supabase) {
        throw new Error("Supabase is not configured");
      }

      const url = window.location.href;
      const hasQueryCode = url.includes("code=");
      const hasHashToken =
        url.includes("access_token=") || url.includes("refresh_token=");

      let sessionData = null;

      if (hasQueryCode) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(url);
        if (error) throw error;
        sessionData = data;
      } else if (hasHashToken) {
        const { data, error } = await supabase.auth.getSessionFromUrl({
          storeSession: true,
        });
        if (error) throw error;
        sessionData = data;
      } else {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        sessionData = data;
      }

      if (!sessionData?.session) {
        throw new Error("No session found. The link may have expired or was already used.");
      }

      // If this is a password recovery link, keep the user on this screen
      // and ask for a new password.
      if (typeFromUrl === "recovery") {
        setStatus("set-password");
        return;
      }

      // For all other link types (magic link, signup confirm, etc.)
      setStatus("success");

      // Wait a moment to show success state
      setTimeout(() => {
        const role = localStorage.getItem("mudo:userRole");

        if (role === "studio") {
          navigate("/dashboard/home", { replace: true });
        } else if (role === "visitor") {
          navigate("/discover", { replace: true });
        } else {
          // No role selected yet, go to role selection
          navigate("/start", { replace: true });
        }
      }, 1000);
    } catch (err) {
      console.error("Auth callback error:", err);
      setStatus("error");
      setError(err.message || "Authentication failed");

      // Redirect to login after showing error
      setTimeout(() => {
        navigate("/auth", { replace: true });
      }, 3000);
    }
  }

  async function handleSetNewPassword() {
    const p = newPass.trim();
    const c = newPassConfirm.trim();

    if (!p || p.length < MIN_PASS_LEN) {
      setPassError(`Password must be at least ${MIN_PASS_LEN} characters long.`);
      return;
    }
    if (p !== c) {
      setPassError("Passwords do not match.");
      return;
    }

    setPassError("");
    setStatus("verifying");
    try {
      const { error } = await supabase.auth.updateUser({ password: p });
      if (error) throw error;

      setStatus("success");

      // After password update, send them back to login so they can sign in with the new password
      setTimeout(() => {
        navigate("/auth", { replace: true });
      }, 1500);
    } catch (err) {
      console.error("Password update error:", err);
      setStatus("set-password");
      setPassError(
        err?.message ||
          "Could not update your password. Please try again or request a new reset link."
      );
    }
  }

  return (
    <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center font-sans relative overflow-hidden px-4">
      <div className="w-full max-w-sm flex flex-col items-center relative">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="w-full mb-8 flex justify-center z-10"
        >
          <img
            src={mudoLogo}
            alt="Mudo Logo"
            className="w-full h-auto object-contain"
            style={{
              filter:
                "drop-shadow(0 0 2px rgba(255,170,0,0.65)) drop-shadow(0 0 3px rgba(80, 80, 80, 0.32))",
            }}
          />
        </motion.div>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative w-full bg-white/5 backdrop-blur-lg border border-white/20 rounded-xl p-8 shadow-xl z-10"
        >
          {/* Top-edge glow */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-[80%] h-5 bg-orange-400/50 blur-lg rounded-full pointer-events-none" />

          <div className="text-center">
            {status === "verifying" && (
              <>
                <div className="mb-4">
                  <div className="inline-block w-12 h-12 border-4 border-orange-400/30 border-t-orange-400 rounded-full animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  {flowLabel}...
                </h2>
                <p className="text-white/60 text-sm">
                  Please wait while we authenticate you.
                </p>
              </>
            )}

            {status === "set-password" && (
              <>
                <div className="mb-4 text-3xl">🔐</div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Set a new password
                </h2>
                <p className="text-white/60 text-sm mb-4">
                  Choose a new password for your Mudo account.
                </p>

                <div className="space-y-3 text-left">
                  <div>
                    <label className="block text-xs text-white/60 mb-1">
                      New password
                    </label>
                    <input
                      type="password"
                      value={newPass}
                      onChange={(e) => setNewPass(e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded-md px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-orange-400/40"
                      placeholder={`At least ${MIN_PASS_LEN} characters`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      value={newPassConfirm}
                      onChange={(e) => setNewPassConfirm(e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded-md px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-orange-400/40"
                      placeholder="Repeat new password"
                    />
                  </div>
                  {passError && (
                    <p className="text-xs text-red-400 mt-1">{passError}</p>
                  )}
                  <button
                    onClick={handleSetNewPassword}
                    className="mt-3 w-full py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-yellow-800 to-orange-900 hover:from-yellow-900 hover:to-orange-900 text-white shadow-[inset_0_0_1px_rgba(255,170,0,0.2)]"
                  >
                    Save new password
                  </button>
                </div>
              </>
            )}

            {status === "success" && (
              <>
                <div className="mb-4 text-5xl">✓</div>
                <h2 className="text-xl font-semibold text-green-400 mb-2">
                  Authentication successful!
                </h2>
                <p className="text-white/60 text-sm">
                  Redirecting you to the app...
                </p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="mb-4 text-5xl">✗</div>
                <h2 className="text-xl font-semibold text-red-400 mb-2">
                  Authentication failed
                </h2>
                <p className="text-white/60 text-sm mb-4">{error}</p>
                <p className="text-white/40 text-xs">
                  Redirecting to login...
                </p>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
